import * as IoC from 'typescript-ioc';
import { action, observable } from 'mobx';
import { alertServerError } from '../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { IBlueprintsDAO } from '../../../api/blueprints/blueprints.dao';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../infrastructure/sub-windows';
import { IntlShape } from 'react-intl';
import { IBlueprintItemDetails } from '../../../api/blueprints/blueprint-details.types';
import { ConnectionsTemplatesForm } from './connections-template-form/connection-template-form';
import { IConnectionTemplateDTO } from '../../../api/blueprints/connection.template.types';
import { TObject } from '../../../infrastructure/type-extensions';
import { convertBlueprintTemplateIntoBlueprintDetails } from '../../../infrastructure/utils/blueprint-details.mapper';
import { CONNECTION_CREATE_TITLE_TEXT, CONNECTION_EDIT_TITLE_TEXT } from './connection-templates.texts';

export class ConnectionTemplatesVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    @observable
    private templates: IConnectionTemplateDTO[] = [];
    @observable
    public isLoading: boolean = false;
    @observable
    public selectedTemplate: IBlueprintItemDetails;
    @observable
    private blueprintIdToEditPopupMap: TObject<ISubWindowFacade> = {};

    constructor(
        private readonly accountId: number,
        private readonly intl: IntlShape,
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
    ) { }

    public get connectionTemplates(): IConnectionTemplateDTO[] {
        return this.templates;
    }

    @action.bound
    public async initialize(): Promise<void> {
        this.startLoading();

        try {
            const response = await this.blueprintsDAO.getConnectionTemplateList({ accountId: this.accountId });
            this.setTemplates(response);
        } catch (error) {
            ConnectionTemplatesVM.handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    public openCreateTemplateForm(): void {
        this.subWindowService.showSubWindow({
            title: this.intl.formatMessage(CONNECTION_CREATE_TITLE_TEXT),
            componentToHost: ConnectionsTemplatesForm,
            componentToHostProps: {
                accountId: this.accountId,
                intl: this.intl,
                apiCallSuccessCallback: () => {
                    this.initialize();
                }
            }
        });
    }

    public openEditTemplateForm(blueprintId: string): ISubWindowFacade {
        return this.subWindowService.showSubWindow({
            title: this.intl.formatMessage(CONNECTION_EDIT_TITLE_TEXT),
            componentToHost: ConnectionsTemplatesForm,
            componentToHostProps: {
                accountId: this.accountId,
                intl: this.intl,
                apiCallSuccessCallback: () => {
                    this.initialize();
                },
                blueprintId: blueprintId,
                deleteCompletionCallback: () => this.completeConnectionTemplateDeletion(blueprintId)
            }
        });
    }

    @action
    public editConnectionTemplate = (blueprintId: string) => {
        if (this.isEditFormHidden(blueprintId)) {
            this.blueprintIdToEditPopupMap[blueprintId] = this.openEditTemplateForm(blueprintId);
        }
    };

    public isEditFormHidden = (blueprintId: string) => {
        const instance = this.blueprintIdToEditPopupMap[blueprintId];
        return !instance || instance.getStatus() === SubWindowStatus.Closed;
    };

    public isTemplateActive = (blueprintId: string): boolean => {
        return this.selectedTemplate?.blueprintId === blueprintId;
    };

    public isTemplateSelected = (blueprintId: string): boolean => {
        return !this.isEditFormHidden(blueprintId) || this.isTemplateActive(blueprintId);
    };

    @action
    public openConnectionTemplateDetails = (blueprintId: string): void => {
        const template = this.templates.find(t => t.blueprintId === blueprintId);
        this.selectedTemplate = convertBlueprintTemplateIntoBlueprintDetails(template.type, template);
    };

    @action
    public closeBluePrintItemDetailsPopover = (): void => {
        this.selectedTemplate = null;
    };

    @action.bound
    private setTemplates(ConnectionTemplates: IConnectionTemplateDTO[]): void {
        this.templates = ConnectionTemplates
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    @action.bound
    private startLoading(): void {
        this.isLoading = true;
    }

    @action.bound
    private stopLoading(): void {
        this.isLoading = false;
    }

    private completeConnectionTemplateDeletion = (deletedBlueprintId: string): void => {
        this.closeEditForm(deletedBlueprintId);
        this.initialize();
    };

    private closeEditForm(blueprintId: string): void {
        this.blueprintIdToEditPopupMap[blueprintId].close();
    }
}
